import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';

import Pagination from 'antd/es/pagination';

import { getSearchParamsFromUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import { ItemsProvider, useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';
import { useMobileView } from '@common/react/hooks/useMobileView';

import LogoLoader, { LogoLoaderPage } from '@commonTuna/react/components/UI/Loaders/LogoLoader';

import { Doctor } from '@app/objects/Doctor';
import DoctorCard from '@app/components/UI/DoctorCard/DoctorCard';
import { ApplicationState } from '@app/store';
import { HeaderState } from '@app/store/HeaderSearch';
import DoctorBookedTimeBlockHeader from '@app/components/UI/DoctorBookedTimeBlock/DoctorBookedTimeBlockHeader';
import { searchFilterActionCreators, SearchFilterState } from '@app/store/SearchFilter';
import Filters from '@app/components/Pages/Doctor/Filters';
import { getAddress, SimpleMap } from '@app/components/Pages/Doctor/Locations';
import { Location } from '@app/objects/Location';

interface Props {
	params: any;
	searchState: HeaderState;
	filterProps?: any;
}

export interface IValues extends SearchFilterState {
	text: string;
}

const getDaysCount = () => {
	if (typeof window === 'undefined') {
		return 6;
	}

	let count = Math.floor((((window.innerWidth * 0.66 - 348) * 0.75 - 78) / 88));

	if (window.innerWidth < 1300) {
		count = Math.floor((((window.innerWidth - 348) * 0.75 - 78) / 88));
	}

	if (window.innerWidth < 1100) {
		count = Math.ceil((window.innerWidth - 350) / 88);
	}

	if (count > 6) {
		return 6;
	}

	if (window.innerWidth < 450) {
		return 2;
	}

	return count > 3 ? count : 3;
};

const getTime = (time) => {
	const [h, m] = time?.split(':');
	return +h * 60 + +m;
};

const DoctorsFormInner: React.FC<Props> = ({
	params,
	searchState,
	filterProps,
}) => {
	const [loadingTimes, setLoadingTimes] = React.useState(false);
	const navigate = useNavigate();

	const dispatch = useDispatch();
	const { updateSearchFilter } = bindActionCreators(searchFilterActionCreators, dispatch);
	const searchFilterData = useSelector((state: ApplicationState) => state.searchFilterData, shallowEqual);
	const [languages, setLanguages] = React.useState<Array<any>>([]);
	const [certificates, setCertificates] = React.useState<Array<any>>();

	const {
		state: {
			filters, items: contextItems, loading, pagination,
		}, actions: { handleChange, load },
	} = useItemsProviderContext<Doctor>();
	const items = React.useMemo(() => {
		return contextItems.map((item) => {
			const locations = item.locations.map((location) => {
				const obj = {};
				location.location.doctorWorkingHours?.forEach((wh) => {
					if (!obj[wh.dayOfWeek]) {
						obj[wh.dayOfWeek] = wh;
					} else {
						obj[wh.dayOfWeek] = {
							...obj[wh.dayOfWeek],
							endTime: getTime(wh.endTime) > getTime(obj[wh.dayOfWeek].endTime) ? wh.endTime : obj[wh.dayOfWeek].endTime,
							startTime: getTime(wh.startTime) > getTime(obj[wh.dayOfWeek].startTime) ? wh.startTime : obj[wh.dayOfWeek].startTime,
						};
					}
				});
				return {
					...location,
					location: {
						...location.location,
						doctorWorkingHours: Object.values(obj) as any,
					},
				};
			});
			return {
				...item,
				locations,
			};
		});
	}, [contextItems]);
	const [location, setLocation] = React.useState<any>(() => {
		return items.find((item) => item?.locations?.[0]?.location)?.locations?.[0]?.location;
	});
	const [locationList, setLocationList] = React.useState<Array<Location>>([]);
	const [selectedDoctorId, setSelectedDoctorId] = React.useState(() =>
		items.find((item) => item?.locations?.[0]?.location)?.id);
	const [key, setKey] = React.useState(1);
	const hideMap = useMobileView(1300);

	const [daysRange, setDaysRange] = React.useState(6);
	const [values, setValues] = React.useState<IValues>({
		locationName: filters.locationId ? searchFilterData.locationName : '',
		globalPayerName: filters.globalPayerId ? searchFilterData.globalPayerName : '',
		globalProcedures: filters.globalProcedureIds ? searchFilterData.globalProcedures : [],
		text: '',
		languages: [],
		certificates: [],
	});

	React.useEffect(() => {
		const obj: any = {};
		items.forEach((doctor) =>
			doctor.locations.forEach((location) => {
				if (!obj[location.location.id]) {
					obj[location.location.id] = location.location;
				}
			}));
		const locations: Array<Location> = Object.values(obj);
		if (locations.some((item) => item.id !== location?.id) && locations.length) {
			setLocation(locations[0]);
		}
		setLocationList(locations);
		setKey(Math.random());
	}, [items]);

	const handleResize = () => {
		setDaysRange(getDaysCount());
	};

	const reloadTimes = (data) => {
		setLoadingTimes(true);
		return load(data, { silent: true })
			.then(() => {
				setLoadingTimes(false);
			});
	};

	React.useEffect(() => {
		if (JSON.stringify({ ...filters })
			!== JSON.stringify({ ...filters, ...searchState, ...params })) {
			handleChange({ ...searchState, ...params })
				.then((res) => {
					const location = res?.list?.[0]?.locations?.[0]?.location;
					location && setLocation(location);
				});
		}
	}, [searchState]);
	const isMobile = useMobileView(undefined, handleResize);

	React.useEffect(() => {
		updateSearchFilter({
			...(filterProps as SearchFilterState),
		});
	}, []);

	React.useEffect(() => {
		setValues((prev) => ({
			...prev,
			...searchFilterData,
		}));
		setLanguages(searchFilterData.languages || []);
		setCertificates(searchFilterData.certificates || []);
	}, [searchFilterData]);

	React.useEffect(() => {
		if (filters.daysRange !== daysRange) {
			reloadTimes({ daysRange });
		}
	}, [filters, daysRange]);

	const onPaginationChange = (current, pageSize) => {
		handleChange({ page: current, pageSize })
			.then((res) => {
				const location = res?.list?.[0]?.locations?.[0]?.location;
				location && setLocation(location);
			});
	};

	const onSelectPreviousWeek = () => {
		return undefined;
	};

	const onSelectNextWeek = () => {
		return undefined;
	};

	const columns = items
		? items.find((item) => item?.doctorBookedTimes?.values)?.doctorBookedTimes.values
		: null;

	const goToPath = (path: string, state: any) => {
		navigate(path, { state });
	};

	return (
		<div className="doctor-card-list doctor-list" style={{ maxWidth: 'min(1400, 100%)', margin: '0 auto', '--count': daysRange } as any}>
			<div style={{ paddingTop: 36 }} />
			<BreadCrumbs
				title="Doctors"
			/>
			<Filters certificates={certificates} languages={languages} daysRange={daysRange} />
			{!loading && !items.length ? <h3 style={{ marginTop: 30 }}>No Doctors found</h3> : <>
				{columns && !loading && <div className="doctor-booked-header__fixed text-center">
					<table>
						<thead>
							<DoctorBookedTimeBlockHeader
								key={daysRange}
								daysRange={daysRange}
								disabledButtons
								leftChevronDisabled
								onSelectPreviousWeek={onSelectPreviousWeek}
								onSelectNextWeek={onSelectNextWeek}
								columns={columns}
							/>
						</thead>
					</table>
					{loadingTimes && <div className="doctor-booked__loader">
						<LogoLoader size={32} />
					</div>}
				</div>}
				<div className="doctor-list-inner">
					{/* {defaultAddressList.map((a) => <button key={a} type="button" onClick={() => setLocation({ addressEn: a })}>
						{a}
					</button>)} */}
					<div>
						{loading
							? <LogoLoaderPage key="loading" />
							: items.map((doctor: Doctor) => (
								<DoctorCard
									{...doctor}
									onClick={() => {
										const location = doctor.locations[0]?.location;
										if (location) {
											setLocation(doctor.locations[0]?.location);
											setSelectedDoctorId(doctor.id);
										}
									}}
									selectLocation={(location) => {
										if (location) {
											setLocation(location);
											setSelectedDoctorId(doctor.id);
										}
									}}
									selectLocationId={location?.id}
									goToPath={goToPath}
									from={filters.from}
									daysRange={daysRange}
									key={doctor.id}
									className={doctor.id === selectedDoctorId ? 'doctor-card__selected' : ''}
								/>
							))}
						{pagination?.total > items.length && <div key="pagination" className="pull-right mt10 mb10">
							<Pagination disabled={loading} onChange={onPaginationChange} onShowSizeChange={onPaginationChange} {...pagination} />
						</div>}
					</div>
					{items.length && !hideMap && location ? <div className="doctor-list-location">
						<SimpleMap key={key} address={getAddress(location)} addressList={locationList} />
					</div> : null}
				</div>
			</>}
		</div>
	);
};

const Doctors: React.FC = () => {
	const location = useLocation();
	const params = getSearchParamsFromUrl(location);
	const { searchState } = useSelector((state: ApplicationState) => ({ searchState: state.header }));

	return (
		<>
			<ServerPageProvider
				path="doctors"
				defaultTitle="Doctors"
				inner={(serverPage) =>
					<ItemsProvider<Doctor>
						loadRequest="DoctorSearchWithTimes"
						rows={serverPage?.items}
						filters={{
							...searchState,
							...params,
							from: searchState.from || +new Date(),
							daysRange: getDaysCount(),
						}}
					>
						<DoctorsFormInner
							searchState={searchState}
							params={params}
							filterProps={serverPage?.filterProps}
						/>
					</ItemsProvider>
				}
			/>
		</>
	);
};

export default Doctors;
